import { Link } from 'react-router-dom'
import SEO from '../components/SEO.jsx'
import { useProducts } from '../context/ProductsContext.jsx'
import './TermsPage.css'

/**
 * Privacy policy — the sibling of TermsPage, and deliberately identical in
 * shape: staff edit it under Content → Privacy Policy, it is stored in
 * catalog_settings as `privacy_content`, and these defaults render until
 * someone saves over them.
 *
 * It shares TermsPage.css rather than duplicating a stylesheet, so the two
 * legal pages can never drift apart visually.
 *
 * The defaults below describe how this system actually behaves — the data it
 * stores, the processors it uses, and the retention that follows from the
 * order and audit records the admin panel keeps. They are a starting point
 * written to be true of a stock deployment, not legal advice; a store with
 * customers in the EU or California in particular should have them reviewed.
 */

export const DEFAULT_PRIVACY_SECTIONS = [
  {
    title: '1. Who We Are',
    body: `LooksLab operates this website and is responsible for the personal information described in this policy. If you have questions about how your information is handled, contact us through our Discord server or the contact address in the footer of this site.

This policy explains what we collect, why we collect it, who we share it with, and what you can ask us to do about it.`,
  },
  {
    title: '2. Information You Give Us',
    body: `When you place an order we collect the name, email address, shipping address, and any optional details you provide such as a Discord username or order notes. This information is required to generate your invoice, ship your order, and contact you about it.

If you create an account we additionally store your password in hashed form — we never store it in a form we can read — along with any saved addresses, your marketing email preference, and your record of agreeing to our Terms of Service.

If you contact our support inbox, we keep that correspondence so staff can see the history of your conversation with us.

We do not collect or store card numbers or bank details. Payment happens outside this website, and we only record that a payment was received and matched to your invoice.`,
  },
  {
    title: '3. Information Collected Automatically',
    body: `We record the IP address used when an account is created and when our terms are accepted. This is kept as evidence of the agreement and to detect fraudulent or duplicated referral activity.

Our hosting provider processes standard technical information — IP address, browser type, and request timestamps — in order to serve the site and protect it from abuse.

We do not use advertising cookies or third-party analytics trackers. Your browser stores a sign-in token when you log in, and a small amount of local data such as your cart, which never leaves your device unless you place an order.`,
  },
  {
    title: '4. How We Use Your Information',
    body: `We use your information to process and fulfil your orders, generate and send invoices, confirm payment, arrange shipping and provide tracking, and answer your support messages.

Where you have an account, we use it to show your order history, saved addresses, rewards balance, and referral activity.

We send marketing email only if you have opted in, and every marketing message includes an unsubscribe link. Order-related email — invoices, payment confirmations, shipping notices — is sent regardless, because it is necessary to complete a purchase you asked for.

We use fraud signals such as repeated addresses and shared sign-up IP addresses to protect the store from abuse of our referral and discount programs.`,
  },
  {
    title: '5. Who We Share It With',
    body: `We do not sell your personal information, and we do not share it for advertising purposes.

We share what is necessary with the service providers who make this store work: our hosting and database provider, our transactional email provider, and the shipping carriers who deliver your order. Each receives only what their part of the job requires — a carrier gets your delivery address, our email provider gets your email address and the message.

We may disclose information where we are legally required to do so, or where it is necessary to establish, exercise, or defend a legal claim.`,
  },
  {
    title: '6. How Long We Keep It',
    body: `Order records, invoices, and their associated shipping and payment history are kept for as long as we are required to retain business and tax records.

Account information is kept while your account is open. If you ask us to delete your account, we remove your profile, saved addresses, and account preferences; your past orders remain on record, unlinked from any account, because they form part of our business records.

Support correspondence and our internal audit log of staff actions are retained so that decisions affecting your orders can be traced after the fact.`,
  },
  {
    title: '7. Your Choices and Rights',
    body: `You can ask us to show you what personal information we hold about you, correct anything that is wrong, or delete your account. Contact us through Discord or the contact address in the footer and we will respond within a reasonable period.

You can unsubscribe from marketing email at any time using the link in any marketing message, or by turning off the marketing preference in your account settings. This does not stop order-related email, which is necessary to complete your purchase.

Depending on where you live you may have additional rights over your personal information, including the right to object to certain processing or to lodge a complaint with your local data protection authority.`,
  },
  {
    title: '8. Security',
    body: `Passwords are stored hashed and are never recoverable in readable form. Access to customer information in our admin system is restricted to authorised staff, and every action staff take against an order or account is recorded in an audit log.

No system is perfectly secure. We take reasonable measures to protect your information, but we cannot guarantee absolute security of information transmitted over the internet.`,
  },
  {
    title: '9. Children',
    body: `This store is not intended for anyone under 18. We do not knowingly collect personal information from children. If you believe a minor has given us their information, contact us and we will delete it.`,
  },
  {
    title: '10. Changes to This Policy',
    body: `We may update this policy from time to time. The revised version will be posted on this page with a new effective date, and continued use of the site after that date constitutes acceptance of the updated policy.`,
  },
  {
    title: '11. Contact',
    body: `If you have questions about this privacy policy or about how your information is handled, reach out through our Discord server or the contact address in the footer of this site.`,
  },
]

export const DEFAULT_PRIVACY_EFFECTIVE_DATE = 'June 2025'

export default function PrivacyPage() {
  const { catalogSettings } = useProducts()

  const saved         = catalogSettings?.privacy_content ?? {}
  const effectiveDate = saved.effectiveDate ?? DEFAULT_PRIVACY_EFFECTIVE_DATE
  const sections      = saved.sections?.length ? saved.sections : DEFAULT_PRIVACY_SECTIONS

  return (
    <div className="container terms">
      <SEO config={{ title: 'Privacy Policy', robots: 'noindex, nofollow', titleRaw: true }} />
      <h1>Privacy Policy</h1>
      <p className="terms__updated">Effective date: {effectiveDate} · Last updated: {effectiveDate}</p>

      {sections.map((sec, i) => (
        <section key={i}>
          <h2>{sec.title}</h2>
          {sec.body.split('\n\n').map((para, pi) => (
            <p key={pi}>{para}</p>
          ))}
        </section>
      ))}

      <p style={{ marginTop: '1.5rem' }}>
        <Link to="/" className="terms__back">← Back to home</Link>
      </p>
    </div>
  )
}
